// filepath: apps/web/src/admin/positions/PositionForm.tsx
//
// ISS-516 — shared position create/edit form component.
//
// Reused by new.tsx and edit.tsx. The component is a controlled
// form with four fields:
//   - title (required, maxLength=255 mirroring the Go Validate
//     function at internal/directory/position/package.go:55-62
//     which rejects empty/whitespace title).
//   - job_code (optional, maxLength=64).
//   - organization_unit_id (optional FK; the form renders an
//     empty-option "no org unit" selector for null).
//   - location_id (optional FK; the form renders an empty-option
//     "no location" selector for null).
//
// INV-2: no server-joined display fields (e.g. unit_name).
// INV-9: no dangerouslySetInnerHTML; every value flows through
// JSX text / <option value>.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { Card } from "@/design-system/Card";
import { Button } from "@/design-system/Button";
import { Banner } from "@/design-system/Banner";

export interface PositionFormValues {
  organization_unit_id: string | null;
  location_id: string | null;
  title: string;
  job_code: string;
}

export interface PositionFormProps {
  /** Initial form values. For new, default to all empty / null.
   * For edit, set from the loader. */
  initial: PositionFormValues;
  /** Optional list of org-unit candidates (id, name). */
  orgUnitCandidates?: { id: string; name: string }[];
  /** Optional list of location candidates (id, name). */
  locationCandidates?: { id: string; name: string }[];
  /** Submit handler. The form passes the trimmed values. */
  onSubmit: (values: PositionFormValues) => Promise<void>;
  /** Optional cancel handler. */
  onCancel?: () => void;
  /** When true, the submit button renders as "Saving…". */
  submitting?: boolean;
  /** Optional error banner. */
  error?: string | null;
  /** When set, the title field has a field-level error. */
  titleError?: string | null;
  /** Title for the submit button. */
  submitLabel: string;
  /** Title for the loading state. */
  loadingLabel: string;
  /** Heading for the form. */
  heading: string;
}

export function PositionForm({
  initial,
  orgUnitCandidates,
  locationCandidates,
  onSubmit,
  onCancel,
  submitting,
  error,
  titleError,
  submitLabel,
  loadingLabel,
  heading,
}: PositionFormProps): React.ReactElement {
  const { t } = useTranslation();
  const [values, setValues] = React.useState<PositionFormValues>(initial);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed: PositionFormValues = {
      organization_unit_id: values.organization_unit_id,
      location_id: values.location_id,
      title: values.title.trim(),
      job_code: values.job_code.trim(),
    };
    await onSubmit(trimmed);
  };

  return (
    <Card>
      <form
        onSubmit={handleSubmit}
        aria-labelledby="position-form-title"
        data-testid="position-form"
      >
        <h1 id="position-form-title">{heading}</h1>
        {error ? (
          <Banner tone="danger" data-testid="position-form-error">
            {error}
          </Banner>
        ) : null}
        <div>
          <label htmlFor="position-form-title-field">
            {t("directory.positions.field.title", "Title")}
          </label>
          <input
            id="position-form-title-field"
            type="text"
            required
            maxLength={255}
            value={values.title}
            onChange={(e) =>
              setValues((v) => ({ ...v, title: e.target.value }))
            }
            aria-invalid={titleError ? true : undefined}
            aria-describedby={
              titleError ? "position-form-title-error" : undefined
            }
            data-testid="position-form-title-input"
          />
          {titleError ? (
            <p
              role="alert"
              id="position-form-title-error"
              data-testid="position-form-title-error"
            >
              {titleError}
            </p>
          ) : null}
        </div>
        <div>
          <label htmlFor="position-form-job-code-field">
            {t("directory.positions.field.job_code", "Job code")}
          </label>
          <input
            id="position-form-job-code-field"
            type="text"
            maxLength={64}
            value={values.job_code}
            onChange={(e) =>
              setValues((v) => ({ ...v, job_code: e.target.value }))
            }
            data-testid="position-form-job-code-input"
          />
        </div>
        <div>
          <label htmlFor="position-form-org-unit-field">
            {t(
              "directory.positions.field.organization_unit",
              "Organization unit",
            )}
          </label>
          <select
            id="position-form-org-unit-field"
            value={values.organization_unit_id ?? ""}
            onChange={(e) =>
              setValues((v) => ({
                ...v,
                organization_unit_id: e.target.value || null,
              }))
            }
            data-testid="position-form-org-unit-select"
          >
            <option value="">
              {t("directory.positions.field.no_org_unit", "(no org unit)")}
            </option>
            {(orgUnitCandidates ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="position-form-location-field">
            {t("directory.positions.field.location", "Location")}
          </label>
          <select
            id="position-form-location-field"
            value={values.location_id ?? ""}
            onChange={(e) =>
              setValues((v) => ({
                ...v,
                location_id: e.target.value || null,
              }))
            }
            data-testid="position-form-location-select"
          >
            <option value="">
              {t("directory.positions.field.no_location", "(no location)")}
            </option>
            {(locationCandidates ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        <div className="position-form-actions">
          <Button
            type="submit"
            variant="primary"
            disabled={submitting || values.title.trim().length === 0}
            data-testid="position-form-submit"
          >
            {submitting ? loadingLabel : submitLabel}
          </Button>
          {onCancel ? (
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              data-testid="position-form-cancel"
            >
              {t("directory.positions.action.cancel", "Cancel")}
            </Button>
          ) : null}
        </div>
      </form>
    </Card>
  );
}
